import React, { useState, useEffect } from 'react';
import {
  X,
  Radio,
  Wifi,
  ShieldAlert,
  CheckCircle2,
  Copy,
  Check,
  Terminal,
  Activity,
  ArrowRight,
  ExternalLink,
  Cpu,
  Layers,
  Sparkles,
  AlertTriangle,
  Play,
  Grid,
  Sliders,
  Maximize2,
  Bed,
  Droplets,
  DoorOpen,
  EyeOff,
  PhoneCall,
  Save
} from 'lucide-react';
import { AmbientRoom, RadarZone, RadarCalibrationConfig } from '../types';
import { playFallAlertSound } from '../services/audioService';

interface AqaraHardwareModalProps {
  isOpen: boolean;
  onClose: () => void;
  rooms: AmbientRoom[];
  onTriggerHardwareFall: (roomId: string) => void;
  onResetHardwareAlert: () => void;
  initialTab?: 'guide' | 'webhook' | 'test' | 'calibration';
  initialRoomId?: string;
}

export const AqaraHardwareModal: React.FC<AqaraHardwareModalProps> = ({
  isOpen,
  onClose,
  rooms,
  onTriggerHardwareFall,
  onResetHardwareAlert,
  initialTab = 'calibration',
  initialRoomId = '101'
}) => {
  const [selectedRoomId, setSelectedRoomId] = useState<string>(initialRoomId);
  const [copiedUrl, setCopiedUrl] = useState<boolean>(false);
  const [copiedCurl, setCopiedCurl] = useState<boolean>(false);
  const [copiedHeartbeat, setCopiedHeartbeat] = useState<boolean>(false);
  const [copiedSpatial, setCopiedSpatial] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<'guide' | 'webhook' | 'calibration' | 'test'>(initialTab);
  const [simulatedPayloadReceived, setSimulatedPayloadReceived] = useState<boolean>(false);
  const [sensorTestStatus, setSensorTestStatus] = useState<string | null>(null);

  // Calibration state for selected room
  const [ceilingHeight, setCeilingHeight] = useState<number>(2.7);
  const [fallSensitivity, setFallSensitivity] = useState<'High' | 'Medium' | 'Low'>('High');
  const [dwellSeconds, setDwellSeconds] = useState<number>(3);
  const [savedConfigSuccess, setSavedConfigSuccess] = useState<boolean>(false);

  // Simulated target coordinates (X: 1-8, Y: 1-8, Z: 0.0 - 2.0m)
  const [currentTarget, setCurrentTarget] = useState<{
    x: number;
    y: number;
    z: number;
    zoneName: string;
    zoneType: string;
    posture: string;
  }>({
    x: 2.5,
    y: 2.0,
    z: 0.85,
    zoneName: 'Bed Zone',
    zoneType: 'bed',
    posture: 'Bed Resting'
  });

  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab);
      if (initialRoomId) setSelectedRoomId(initialRoomId);
    }
  }, [isOpen, initialTab, initialRoomId]);

  if (!isOpen) return null;

  const selectedRoomObj = rooms.find((r) => r.roomId === selectedRoomId) || rooms[0];

  const secretApiKey = 'mc-radar-sec-7f9a2b4e8c1d3e5f';
  const webhookCloudUrl = `https://us-central1-rugged-alcove-388517.cloudfunctions.net/aqaraRadarWebhook?key=${secretApiKey}`;
  const webhookBaseUrl = 'https://us-central1-rugged-alcove-388517.cloudfunctions.net/aqaraRadarWebhook';

  const curlCommand = `curl -X POST "${webhookBaseUrl}" \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: ${secretApiKey}" \\
  -d '{"roomNumber":"${selectedRoomId}","event":"fall_detected","deviceId":"aqara-fp2-living","posture":"Fall Alert"}'`;

  const heartbeatCurlCommand = `curl -X POST "${webhookBaseUrl}" \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: ${secretApiKey}" \\
  -d '{"roomNumber":"${selectedRoomId}","event":"heartbeat","deviceId":"aqara-fp2-living","rssi":"-54 dBm"}'`;

  const spatialCurlCommand = `curl -X POST "${webhookBaseUrl}" \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: ${secretApiKey}" \\
  -d '{"roomNumber":"${selectedRoomId}","event":"spatial_telemetry","target":{"x":${currentTarget.x},"y":${currentTarget.y},"z":${currentTarget.z},"zone":"${currentTarget.zoneType}"}}'`;

  const handleCopy = (text: string, type: 'url' | 'curl' | 'heartbeat' | 'spatial') => {
    navigator.clipboard.writeText(text);
    if (type === 'url') {
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2000);
    } else if (type === 'curl') {
      setCopiedCurl(true);
      setTimeout(() => setCopiedCurl(false), 2000);
    } else if (type === 'heartbeat') {
      setCopiedHeartbeat(true);
      setTimeout(() => setCopiedHeartbeat(false), 2000);
    } else {
      setCopiedSpatial(true);
      setTimeout(() => setCopiedSpatial(false), 2000);
    }
  };

  const handleSaveCalibrationToCloud = async () => {
    try {
      await fetch(
        `https://us-central1-rugged-alcove-388517.cloudfunctions.net/aqaraRadarWebhook?action=save_calibration&room=${selectedRoomId}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            roomNumber: selectedRoomId,
            ceilingHeightMeters: ceilingHeight,
            fallSensitivity,
            fallDwellSeconds: dwellSeconds,
            updatedAt: new Date().toISOString()
          })
        }
      );
      setSavedConfigSuccess(true);
      setTimeout(() => setSavedConfigSuccess(false), 2500);
    } catch (e) {
      //
    }
  };

  const handleSimulateMovement = async (mode: 'bed' | 'walk' | 'fall') => {
    if (mode === 'bed') {
      setCurrentTarget({
        x: 2.5,
        y: 2.0,
        z: 0.85,
        zoneName: 'Bed Zone',
        zoneType: 'bed_zone',
        posture: 'Bed Resting'
      });
      fetch(`${webhookBaseUrl}?key=${secretApiKey}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          roomNumber: selectedRoomId,
          event: 'spatial_telemetry',
          target: { x: 2.5, y: 2.0, z: 0.85, zone: 'bed_zone' }
        })
      }).catch(() => {});
    } else if (mode === 'walk') {
      setCurrentTarget({
        x: 4.0,
        y: 3.5,
        z: 1.65,
        zoneName: 'Corridor Ambulation',
        zoneType: 'ambulation_zone',
        posture: 'Walking'
      });
      fetch(`${webhookBaseUrl}?key=${secretApiKey}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          roomNumber: selectedRoomId,
          event: 'spatial_telemetry',
          target: { x: 4.0, y: 3.5, z: 1.65, zone: 'ambulation_zone' }
        })
      }).catch(() => {});
    } else if (mode === 'fall') {
      setCurrentTarget({
        x: 3.8,
        y: 5.2,
        z: 0.18,
        zoneName: 'Floor Fall Zone (CRITICAL DROP)',
        zoneType: 'floor_fall_zone',
        posture: 'Fall Alert'
      });
      playFallAlertSound();
      setSimulatedPayloadReceived(true);
      onTriggerHardwareFall(selectedRoomId);

      // Transmit to Cloud Webhook to trigger actual Telephony Voice Escalation
      fetch(`${webhookBaseUrl}?key=${secretApiKey}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          roomNumber: selectedRoomId,
          event: 'fall_detected',
          deviceId: `aqara-fp2-room${selectedRoomId}`,
          posture: 'Fall Alert',
          target: { x: 3.8, y: 5.2, z: 0.18, zone: 'floor_fall_zone' }
        })
      }).catch(() => {});

      setTimeout(() => {
        setSimulatedPayloadReceived(false);
      }, 4000);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-slate-900 border-2 border-slate-700 rounded-3xl w-full max-w-4xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between bg-slate-950/90">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-2xl bg-teal-500/20 border border-teal-500/40 text-teal-300">
              <Radio className="h-6 w-6 text-teal-400 animate-pulse" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-extrabold text-white text-base">
                  Aqara Presence Sensor FP2 Hardware &amp; Zone Calibration
                </h3>
                <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-teal-500/20 text-teal-300 border border-teal-500/40 font-bold uppercase">
                  60GHz mmWave Radar
                </span>
              </div>
              <p className="text-[11px] text-slate-400">
                Non-wearable, privacy-preserving vertical velocity tracking &amp; spatial multi-zone telemetry.
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation Tabs */}
        <div className="flex items-center px-6 pt-3 border-b border-slate-800 bg-slate-950/50 gap-4 text-xs font-semibold overflow-x-auto">
          <button
            onClick={() => setActiveTab('calibration')}
            className={`pb-3 border-b-2 transition flex items-center gap-2 shrink-0 ${
              activeTab === 'calibration'
                ? 'border-teal-400 text-teal-300'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <Grid className="h-4 w-4" />
            <span>Spatial Radar Map &amp; Zone Calibration</span>
          </button>

          <button
            onClick={() => setActiveTab('guide')}
            className={`pb-3 border-b-2 transition flex items-center gap-2 shrink-0 ${
              activeTab === 'guide'
                ? 'border-teal-400 text-teal-300'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <Layers className="h-4 w-4" />
            <span>Mounting &amp; Altitude Guide</span>
          </button>

          <button
            onClick={() => setActiveTab('webhook')}
            className={`pb-3 border-b-2 transition flex items-center gap-2 shrink-0 ${
              activeTab === 'webhook'
                ? 'border-teal-400 text-teal-300'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <Wifi className="h-4 w-4" />
            <span>Webhook &amp; HomeKit Setup</span>
          </button>

          <button
            onClick={() => setActiveTab('test')}
            className={`pb-3 border-b-2 transition flex items-center gap-2 shrink-0 ${
              activeTab === 'test'
                ? 'border-teal-400 text-teal-300'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <Terminal className="h-4 w-4" />
            <span>Raw Test Payload Console</span>
          </button>
        </div>

        {/* Body Content */}
        <div className="p-6 space-y-5 max-h-[72vh] overflow-y-auto text-xs">
          {/* TAB: SPATIAL ZONE CALIBRATION */}
          {activeTab === 'calibration' && (
            <div className="space-y-5">
              {/* Room Selector & Status Header */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-2xl bg-slate-950 border border-slate-800">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-xl bg-teal-500/20 text-teal-300 border border-teal-500/40">
                    <Sliders className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-white text-sm">
                      {selectedRoomObj?.roomNumber || `Room ${selectedRoomId}`} Spatial Radar Grid Calibration
                    </h4>
                    <p className="text-slate-400 text-[11px]">
                      Resident: <strong>{selectedRoomObj?.residentName || 'Eleanor Vance'}</strong> ({selectedRoomObj?.wing || 'Wing-A'}) • 60GHz Doppler FMCW • 5.0m &times; 5.0m Field of View
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <select
                    value={selectedRoomId}
                    onChange={(e) => setSelectedRoomId(e.target.value)}
                    className="bg-slate-900 border border-slate-700 text-teal-300 font-mono text-[11px] rounded-xl px-2.5 py-1 focus:outline-none focus:border-teal-500"
                  >
                    {rooms.map((r) => (
                      <option key={r.roomId} value={r.roomId}>
                        {r.roomNumber} — {r.residentName}
                      </option>
                    ))}
                  </select>
                  <span className="text-[11px] font-mono text-emerald-400 bg-emerald-500/10 px-2.5 py-1 rounded-xl border border-emerald-500/30 flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-full bg-emerald-400 animate-ping" />
                    <span>Live Tracking Calibrated</span>
                  </span>
                </div>
              </div>

              {/* Main Grid & Spatial Target Visualizer */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
                {/* 8x8 Spatial Radar Radar Canvas */}
                <div className="lg:col-span-7 p-4 rounded-2xl bg-slate-950 border border-slate-800 flex flex-col justify-between">
                  <div className="flex items-center justify-between pb-3 border-b border-slate-800/80 mb-3">
                    <span className="font-bold text-white text-xs flex items-center gap-1.5">
                      <Radio className="h-4 w-4 text-teal-400" />
                      <span>Ceiling Radar Field of View (Top-Down Bird's Eye Map)</span>
                    </span>
                    <span className="text-[10px] font-mono text-slate-500">Scale: 1 grid = 0.625m</span>
                  </div>

                  {/* 8x8 Visual Grid Container */}
                  <div className="relative aspect-square w-full bg-slate-900/90 rounded-2xl border-2 border-slate-800 p-2 overflow-hidden flex flex-col justify-between shadow-inner">
                    {/* Zone Overlays */}
                    {/* Zone 1: Bed Zone (Top Left: cols 1-4, rows 1-3) */}
                    <div
                      className="absolute rounded-xl border-2 border-sky-500/60 bg-sky-500/15 p-2 pointer-events-none transition-all"
                      style={{ top: '3%', left: '3%', width: '46%', height: '34%' }}
                    >
                      <span className="text-[10px] font-mono font-bold text-sky-300 flex items-center gap-1">
                        <Bed className="h-3 w-3" />
                        <span>Bed Zone</span>
                      </span>
                    </div>

                    {/* Zone 2: Bathroom Zone (Top Right: cols 5-8, rows 1-3) */}
                    <div
                      className="absolute rounded-xl border-2 border-indigo-500/60 bg-indigo-500/15 p-2 pointer-events-none transition-all"
                      style={{ top: '3%', left: '52%', width: '45%', height: '34%' }}
                    >
                      <span className="text-[10px] font-mono font-bold text-indigo-300 flex items-center gap-1">
                        <Droplets className="h-3 w-3" />
                        <span>Bathroom</span>
                      </span>
                    </div>

                    {/* Zone 3: Floor / Critical Fall Zone (Center: cols 2-6, rows 4-6) */}
                    <div
                      className={`absolute rounded-xl border-2 p-2 pointer-events-none transition-all ${
                        currentTarget.z < 0.35
                          ? 'border-rose-500 bg-rose-500/30 animate-pulse ring-2 ring-rose-500'
                          : 'border-rose-500/50 bg-rose-500/10'
                      }`}
                      style={{ top: '40%', left: '15%', width: '60%', height: '36%' }}
                    >
                      <span className="text-[10px] font-mono font-black text-rose-300 flex items-center gap-1">
                        <ShieldAlert className="h-3.5 w-3.5 text-rose-400" />
                        <span>Floor / Impact Fall Zone</span>
                      </span>
                    </div>

                    {/* Zone 4: Doorway Exit (Bottom Left: cols 1-3, rows 7-8) */}
                    <div
                      className="absolute rounded-xl border-2 border-amber-500/60 bg-amber-500/15 p-1.5 pointer-events-none transition-all"
                      style={{ top: '78%', left: '3%', width: '35%', height: '19%' }}
                    >
                      <span className="text-[9.5px] font-mono font-bold text-amber-300 flex items-center gap-1">
                        <DoorOpen className="h-3 w-3" />
                        <span>Doorway Exit</span>
                      </span>
                    </div>

                    {/* Zone 5: Interference Filter (Bottom Right: cols 7-8, rows 7-8) */}
                    <div
                      className="absolute rounded-xl border border-slate-700 bg-slate-800/40 p-1.5 pointer-events-none transition-all"
                      style={{ top: '78%', left: '72%', width: '25%', height: '19%' }}
                    >
                      <span className="text-[9px] font-mono text-slate-400 flex items-center gap-1">
                        <EyeOff className="h-2.5 w-2.5" />
                        <span>Fan Filter</span>
                      </span>
                    </div>

                    {/* Concentric Radar Grid Crosshairs */}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-20">
                      <div className="w-3/4 h-3/4 rounded-full border border-teal-400" />
                      <div className="absolute w-1/2 h-1/2 rounded-full border border-teal-400" />
                    </div>

                    {/* Animated Live Radar Target Dot */}
                    <div
                      className="absolute transition-all duration-700 ease-out z-10 -translate-x-1/2 -translate-y-1/2"
                      style={{
                        left: `${(currentTarget.x / 8) * 100}%`,
                        top: `${(currentTarget.y / 8) * 100}%`
                      }}
                    >
                      <div className="relative flex items-center justify-center">
                        <div
                          className={`absolute h-8 w-8 rounded-full opacity-75 animate-ping ${
                            currentTarget.z < 0.35 ? 'bg-rose-500' : 'bg-teal-400'
                          }`}
                        />
                        <div
                          className={`relative h-4 w-4 rounded-full border-2 border-white shadow-lg ${
                            currentTarget.z < 0.35 ? 'bg-rose-600' : 'bg-teal-500'
                          }`}
                        />
                        <span
                          className={`absolute -bottom-6 font-mono font-extrabold text-[9px] px-1.5 py-0.5 rounded shadow-md whitespace-nowrap ${
                            currentTarget.z < 0.35
                              ? 'bg-rose-600 text-white'
                              : 'bg-slate-950 text-teal-300 border border-teal-500/50'
                          }`}
                        >
                          Z={currentTarget.z}m ({currentTarget.posture})
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Target Telemetry Coordinate Readout */}
                  <div className="mt-4 p-3 rounded-xl bg-slate-900 border border-slate-800 grid grid-cols-2 sm:grid-cols-4 gap-2 text-[10.5px] font-mono">
                    <div>
                      <span className="text-slate-500 block text-[9.5px]">X (LATERAL)</span>
                      <strong className="text-white">{currentTarget.x.toFixed(2)} m</strong>
                    </div>
                    <div>
                      <span className="text-slate-500 block text-[9.5px]">Y (LONGITUDINAL)</span>
                      <strong className="text-white">{currentTarget.y.toFixed(2)} m</strong>
                    </div>
                    <div>
                      <span className="text-slate-500 block text-[9.5px]">ALTITUDE (Z)</span>
                      <strong className={currentTarget.z < 0.35 ? 'text-rose-400 font-extrabold' : 'text-emerald-400'}>
                        {currentTarget.z.toFixed(2)} m
                      </strong>
                    </div>
                    <div>
                      <span className="text-slate-500 block text-[9.5px]">ZONE POSTURE</span>
                      <strong className="text-teal-300 truncate block">{currentTarget.posture}</strong>
                    </div>
                  </div>
                </div>

                {/* Right Calibration Controls Panel */}
                <div className="lg:col-span-5 space-y-4 flex flex-col justify-between">
                  {/* Calibration Sliders */}
                  <div className="p-4 rounded-2xl bg-slate-950 border border-slate-800 space-y-3.5">
                    <span className="font-bold text-white text-xs block">
                      Physical Radar Installation Tuning:
                    </span>

                    {/* 1. Ceiling Mount Altitude Slider */}
                    <div className="space-y-1">
                      <div className="flex items-center justify-between text-[11px]">
                        <span className="text-slate-300 font-medium">Ceiling Mount Altitude:</span>
                        <span className="font-mono font-bold text-teal-300">{ceilingHeight.toFixed(1)} meters</span>
                      </div>
                      <input
                        type="range"
                        min="2.4"
                        max="3.2"
                        step="0.1"
                        value={ceilingHeight}
                        onChange={(e) => setCeilingHeight(parseFloat(e.target.value))}
                        className="w-full accent-teal-400 cursor-pointer"
                      />
                      <span className="text-[10px] text-slate-500 block">
                        Recommended: 2.7m (9 ft) directly above bed/floor boundary.
                      </span>
                    </div>

                    {/* 2. Fall Sensitivity Selector */}
                    <div className="space-y-1">
                      <span className="text-slate-300 text-[11px] font-medium block">
                        Vertical Drop Sensitivity (Z-Axis Velocity):
                      </span>
                      <div className="grid grid-cols-3 gap-2">
                        {(['High', 'Medium', 'Low'] as const).map((level) => (
                          <button
                            key={level}
                            type="button"
                            onClick={() => setFallSensitivity(level)}
                            className={`py-1.5 rounded-xl font-bold font-mono text-[11px] border transition ${
                              fallSensitivity === level
                                ? 'bg-teal-500/20 text-teal-300 border-teal-500/50'
                                : 'bg-slate-900 text-slate-400 border-slate-800 hover:text-white'
                            }`}
                          >
                            {level}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* 3. Dwell Confirmation Time */}
                    <div className="space-y-1">
                      <div className="flex items-center justify-between text-[11px]">
                        <span className="text-slate-300 font-medium">Floor Dwell Alert Threshold:</span>
                        <span className="font-mono font-bold text-teal-300">{dwellSeconds} seconds</span>
                      </div>
                      <input
                        type="range"
                        min="2"
                        max="8"
                        step="1"
                        value={dwellSeconds}
                        onChange={(e) => setDwellSeconds(parseInt(e.target.value))}
                        className="w-full accent-teal-400 cursor-pointer"
                      />
                      <span className="text-[10px] text-slate-500 block">
                        Sustained floor-level presence required to prevent false alarms.
                      </span>
                    </div>

                    {/* Save Button */}
                    <button
                      type="button"
                      onClick={handleSaveCalibrationToCloud}
                      className="w-full py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold text-xs transition flex items-center justify-center gap-1.5 active:scale-95 border border-slate-700"
                    >
                      {savedConfigSuccess ? <Check className="h-4 w-4 text-emerald-400" /> : <Save className="h-4 w-4 text-teal-400" />}
                      <span>{savedConfigSuccess ? 'Saved to Cloud Firestore' : 'Save Calibration to Cloud'}</span>
                    </button>
                  </div>

                  {/* Quick Movement & Fall Trigger Simulators */}
                  <div className="p-4 rounded-2xl bg-slate-950 border border-slate-800 space-y-2.5">
                    <span className="font-bold text-white text-xs block">
                      Live Spatial Radar Simulation Controls:
                    </span>

                    <div className="space-y-2">
                      <button
                        type="button"
                        onClick={() => handleSimulateMovement('bed')}
                        className="w-full p-2.5 rounded-xl bg-slate-900 border border-sky-500/40 hover:bg-sky-950/30 text-left transition flex items-center justify-between"
                      >
                        <div className="flex items-center gap-2">
                          <Bed className="h-4 w-4 text-sky-400" />
                          <span className="font-bold text-sky-300 text-xs">Simulate Bed Resting</span>
                        </div>
                        <span className="text-[10px] font-mono text-slate-400">Z=0.85m</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => handleSimulateMovement('walk')}
                        className="w-full p-2.5 rounded-xl bg-slate-900 border border-teal-500/40 hover:bg-teal-950/30 text-left transition flex items-center justify-between"
                      >
                        <div className="flex items-center gap-2">
                          <Activity className="h-4 w-4 text-teal-400" />
                          <span className="font-bold text-teal-300 text-xs">Simulate Ambulation (Walking)</span>
                        </div>
                        <span className="text-[10px] font-mono text-slate-400">Z=1.65m</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => handleSimulateMovement('fall')}
                        className="w-full p-3 rounded-xl bg-gradient-to-r from-rose-600 to-rose-700 hover:from-rose-500 hover:to-rose-600 text-white font-black text-xs transition flex items-center justify-between shadow-lg shadow-rose-950/60 active:scale-95"
                      >
                        <div className="flex items-center gap-2">
                          <ShieldAlert className="h-4 w-4 text-white animate-pulse" />
                          <span>Simulate Sudden Floor Impact Fall</span>
                        </div>
                        <span className="text-[10px] font-mono text-rose-200">Z=0.18m (STAT 911)</span>
                      </button>
                    </div>

                    <p className="text-[10.5px] text-slate-400 leading-relaxed">
                      Testing floor impact immediately updates the dashboard, sounds hospital alarms, and initiates the <strong>Twilio emergency voice call to (408) 391-0924</strong>!
                    </p>
                  </div>
                </div>
              </div>

              {/* Physical Sensor Integration Snippet */}
              <div className="p-4 rounded-2xl bg-slate-950 border border-slate-800 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-white text-xs flex items-center gap-1.5">
                    <Terminal className="h-3.5 w-3.5 text-teal-400" />
                    <span>Physical Device cURL / Home Assistant Rest Command Payload:</span>
                  </span>
                  <button
                    onClick={() => handleCopy(spatialCurlCommand, 'spatial')}
                    className="text-[11px] font-mono text-teal-400 hover:text-teal-300 flex items-center gap-1"
                  >
                    {copiedSpatial ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />}
                    <span>{copiedSpatial ? 'Copied' : 'Copy cURL'}</span>
                  </button>
                </div>

                <div className="p-3 rounded-xl bg-slate-900 border border-slate-800 font-mono text-[11px] text-teal-300 overflow-x-auto select-all">
                  {spatialCurlCommand}
                </div>
              </div>
            </div>
          )}

          {/* TAB: MOUNTING GUIDE */}
          {activeTab === 'guide' && (
            <div className="space-y-4">
              <div className="p-4 rounded-2xl bg-slate-950 border border-slate-800 flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
                <div className="text-xs space-y-1">
                  <h4 className="font-bold text-amber-200">Critical Installation Requirement for Fall Detection</h4>
                  <p className="text-slate-400 leading-relaxed">
                    The Aqara FP2 has two distinct modes: <strong>Zone Tracking</strong> (wall-mounted) and <strong>Fall Detection</strong> (ceiling-mounted). To compute vertical velocity drop, <strong>ceiling mounting is strictly mandatory</strong>.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                <div className="p-4 rounded-2xl bg-slate-950 border border-slate-800">
                  <span className="text-[10px] font-mono text-teal-400 font-bold block mb-1">STEP 1</span>
                  <h5 className="font-bold text-white text-sm">Ceiling Mount Altitude</h5>
                  <p className="text-slate-400 mt-1 leading-relaxed">
                    Install on ceiling at <strong>2.5m – 2.8m (8 – 9.2 ft)</strong> height. Point the face directly downward toward the primary bed/fall risk zone.
                  </p>
                  <div className="mt-2 text-[10px] font-mono text-slate-500 bg-slate-900 px-2 py-1 rounded">
                    Coverage: 4m (13 ft) radius
                  </div>
                </div>

                <div className="p-4 rounded-2xl bg-slate-950 border border-slate-800">
                  <span className="text-[10px] font-mono text-teal-400 font-bold block mb-1">STEP 2</span>
                  <h5 className="font-bold text-white text-sm">Aqara Home App Mode</h5>
                  <p className="text-slate-400 mt-1 leading-relaxed">
                    Pair in <strong>Aqara Home app</strong> (2.4GHz Wi-Fi). Go to Device Settings &rarr; <strong>Detection Mode</strong> &rarr; Select <strong>"Fall Detection"</strong>.
                  </p>
                  <div className="mt-2 text-[10px] font-mono text-slate-500 bg-slate-900 px-2 py-1 rounded">
                    Sensitivity: High • Dwell: 3s
                  </div>
                </div>

                <div className="p-4 rounded-2xl bg-slate-950 border border-slate-800">
                  <span className="text-[10px] font-mono text-teal-400 font-bold block mb-1">STEP 3</span>
                  <h5 className="font-bold text-white text-sm">Exclusion Zones</h5>
                  <p className="text-slate-400 mt-1 leading-relaxed">
                    Configure the room map in the app to mark curtains, ceiling fans, or robot vacuums as <strong>Interference Sources</strong> to eliminate false alerts.
                  </p>
                  <div className="mt-2 text-[10px] font-mono text-slate-500 bg-slate-900 px-2 py-1 rounded">
                    mmWave FMCW Filtered
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB: WEBHOOK SETUP */}
          {activeTab === 'webhook' && (
            <div className="space-y-4 text-xs">
              <div className="p-4 rounded-2xl bg-slate-950 border border-slate-800 space-y-3">
                <span className="font-bold text-slate-200 block text-xs">
                  MediCore Unified Multi-Sensor Cloud Webhook URL (Aqara FP2 + Zigbee/Matter Contact + Bed Pad + Cellular RPM):
                </span>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    readOnly
                    value={webhookCloudUrl}
                    className="flex-1 bg-slate-900 border border-slate-800 rounded-xl px-3 py-2 text-[11px] font-mono text-teal-300 select-all"
                  />
                  <button
                    onClick={() => handleCopy(webhookCloudUrl, 'url')}
                    className="px-3 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold transition flex items-center gap-1.5"
                  >
                    {copiedUrl ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />}
                    <span>{copiedUrl ? 'Copied' : 'Copy URL'}</span>
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="p-4 rounded-2xl bg-slate-950 border border-slate-800 space-y-2">
                  <h4 className="font-bold text-white text-sm">
                    1. Aqara Presence Sensor FP2 (60GHz Radar)
                  </h4>
                  <div className="p-3 rounded-xl bg-slate-900 border border-slate-800 space-y-1.5 leading-relaxed text-slate-300">
                    <strong className="text-teal-300 font-bold block">Apple HomeKit / iPhone Shortcuts or Home Assistant</strong>
                    <p>• Event <code>"fall_detected"</code> &rarr; Triggers STAT 911 Twilio voice call + bedside SBAR.</p>
                    <p>• Event <code>"spatial_telemetry"</code> &rarr; Streams <code>{"target: {x, y, z, zone}"}</code> coordinates.</p>
                    <p>• Event <code>"bed_exit"</code> &rarr; Logs boundary exit without false 911 phone alarm.</p>
                  </div>
                </div>

                <div className="p-4 rounded-2xl bg-slate-950 border border-slate-800 space-y-2">
                  <h4 className="font-bold text-white text-sm">
                    2. Companion Sensors (Contact ADL, Bed &amp; Cellular Vitals)
                  </h4>
                  <div className="p-3 rounded-xl bg-slate-900 border border-slate-800 space-y-1.5 leading-relaxed text-slate-300">
                    <strong className="text-cyan-300 font-bold block">Works Alongside Aqara FP2 on the Same Endpoint</strong>
                    <p>• <strong>Fridge / Med Cabinet (Aqara P2/T1 Contact):</strong> <code>{"event: \"adl_event\", sensorType: \"fridge\" | \"med_cabinet\""}</code></p>
                    <p>• <strong>Bathroom UTI Sensor:</strong> <code>{"event: \"uti_spike\", bathroomVisitsToday: 8"}</code></p>
                    <p>• <strong>Cellular BP &amp; Scale:</strong> <code>{"event: \"vitals_reading\", bp: \"124/78 mmHg\", weightLbs: 184.2"}</code></p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB: RAW PAYLOAD CONSOLE */}
          {activeTab === 'test' && (
            <div className="space-y-4 text-xs">
              <div className="p-4 rounded-2xl bg-slate-950 border border-slate-800 space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-3 border-b border-slate-800">
                  <div>
                    <span className="font-bold text-slate-100 block text-sm">Multi-Sensor Live Webhook Test Console</span>
                    <span className="text-[11px] text-slate-400">
                      Dispatch real cloud webhook payloads for Aqara FP2 Radar or any companion sensor into the selected room.
                    </span>
                  </div>
                  <select
                    value={selectedRoomId}
                    onChange={(e) => setSelectedRoomId(e.target.value)}
                    className="bg-slate-900 border border-slate-700 text-teal-300 font-mono text-xs rounded-xl px-3 py-1.5 focus:outline-none focus:border-teal-500"
                  >
                    {rooms.map((r) => (
                      <option key={r.roomId} value={r.roomId}>
                        {r.roomNumber} — {r.residentName} ({r.wing})
                      </option>
                    ))}
                  </select>
                </div>

                {sensorTestStatus && (
                  <div className="p-3 rounded-xl bg-teal-500/15 border border-teal-500/40 text-teal-200 font-mono text-[11px] flex items-center justify-between">
                    <span>{sensorTestStatus}</span>
                    <CheckCircle2 className="h-4 w-4 text-teal-400 shrink-0" />
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                  <button
                    onClick={() => handleSimulateMovement('fall')}
                    className="p-3 rounded-xl bg-gradient-to-r from-rose-600 to-rose-700 hover:from-rose-500 hover:to-rose-600 text-white font-bold text-xs transition flex items-center justify-between shadow-lg shadow-rose-900/40 active:scale-95"
                  >
                    <span className="flex items-center gap-2">
                      <ShieldAlert className="h-4 w-4 animate-pulse shrink-0" />
                      <span>1. Aqara FP2 Fall Alert</span>
                    </span>
                    <span className="text-[10px] font-mono bg-black/25 px-1.5 py-0.5 rounded">911 Call</span>
                  </button>

                  <button
                    onClick={async () => {
                      setSensorTestStatus(`Transmitting Bed-Exit sensor payload for Room ${selectedRoomId}...`);
                      await fetch(`${webhookBaseUrl}?key=${secretApiKey}`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          roomNumber: selectedRoomId,
                          event: 'bed_exit',
                          deviceId: `bed-pad-room${selectedRoomId}`,
                          posture: 'Standing',
                          bedState: 'Edge of Bed'
                        })
                      }).catch(() => {});
                      setSensorTestStatus(`✅ Bed-Exit sensor webhook delivered for Room ${selectedRoomId} (no false 911 call).`);
                    }}
                    className="p-3 rounded-xl bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/40 text-amber-200 font-bold text-xs transition flex items-center justify-between active:scale-95"
                  >
                    <span className="flex items-center gap-2">
                      <Bed className="h-4 w-4 text-amber-400 shrink-0" />
                      <span>2. Bed-Exit Sensor</span>
                    </span>
                    <span className="text-[10px] font-mono text-amber-300">Edge of Bed</span>
                  </button>

                  <button
                    onClick={async () => {
                      setSensorTestStatus(`Transmitting Fridge ADL Contact Sensor payload for Room ${selectedRoomId}...`);
                      await fetch(`${webhookBaseUrl}?key=${secretApiKey}`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          roomNumber: selectedRoomId,
                          event: 'adl_event',
                          sensorType: 'fridge',
                          deviceId: `aqara-contact-fridge-${selectedRoomId}`
                        })
                      }).catch(() => {});
                      setSensorTestStatus(`✅ Aqara Fridge Contact Sensor verified nutrition activity for Room ${selectedRoomId}.`);
                    }}
                    className="p-3 rounded-xl bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/40 text-emerald-200 font-bold text-xs transition flex items-center justify-between active:scale-95"
                  >
                    <span className="flex items-center gap-2">
                      <DoorOpen className="h-4 w-4 text-emerald-400 shrink-0" />
                      <span>3. Fridge Contact Sensor</span>
                    </span>
                    <span className="text-[10px] font-mono text-emerald-300">ADL Meal OK</span>
                  </button>

                  <button
                    onClick={async () => {
                      setSensorTestStatus(`Transmitting Med Cabinet Contact Sensor payload for Room ${selectedRoomId}...`);
                      await fetch(`${webhookBaseUrl}?key=${secretApiKey}`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          roomNumber: selectedRoomId,
                          event: 'adl_event',
                          sensorType: 'med_cabinet',
                          deviceId: `aqara-contact-meds-${selectedRoomId}`
                        })
                      }).catch(() => {});
                      setSensorTestStatus(`✅ Aqara Med Cabinet Sensor verified pillbox access for Room ${selectedRoomId}.`);
                    }}
                    className="p-3 rounded-xl bg-cyan-500/15 hover:bg-cyan-500/25 border border-cyan-500/40 text-cyan-200 font-bold text-xs transition flex items-center justify-between active:scale-95"
                  >
                    <span className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-cyan-400 shrink-0" />
                      <span>4. Med Cabinet Sensor</span>
                    </span>
                    <span className="text-[10px] font-mono text-cyan-300">ADL Meds OK</span>
                  </button>

                  <button
                    onClick={async () => {
                      setSensorTestStatus(`Transmitting Bathroom Occupancy UTI Spike payload for Room ${selectedRoomId}...`);
                      await fetch(`${webhookBaseUrl}?key=${secretApiKey}`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          roomNumber: selectedRoomId,
                          event: 'uti_spike',
                          sensorType: 'bathroom',
                          bathroomVisitsToday: 8,
                          deviceId: `aqara-bathroom-${selectedRoomId}`
                        })
                      }).catch(() => {});
                      setSensorTestStatus(`✅ Bathroom Sensor logged 8 visits (UTI Spike) for Room ${selectedRoomId}.`);
                    }}
                    className="p-3 rounded-xl bg-purple-500/15 hover:bg-purple-500/25 border border-purple-500/40 text-purple-200 font-bold text-xs transition flex items-center justify-between active:scale-95"
                  >
                    <span className="flex items-center gap-2">
                      <Droplets className="h-4 w-4 text-purple-400 shrink-0" />
                      <span>5. Bathroom UTI Sensor</span>
                    </span>
                    <span className="text-[10px] font-mono text-purple-300">8 Visits</span>
                  </button>

                  <button
                    onClick={async () => {
                      setSensorTestStatus(`Transmitting Cellular LTE-M BP & Scale telemetry for Room ${selectedRoomId}...`);
                      await fetch(`${webhookBaseUrl}?key=${secretApiKey}`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          roomNumber: selectedRoomId,
                          event: 'vitals_reading',
                          bp: '124/78 mmHg',
                          pulseBpm: 72,
                          weightLbs: 158.4,
                          weightDelta48hLbs: 0.2,
                          deviceId: `cellular-rpm-${selectedRoomId}`
                        })
                      }).catch(() => {});
                      setSensorTestStatus(`✅ Cellular BP (124/78) & Weight Scale synced for Room ${selectedRoomId}.`);
                    }}
                    className="p-3 rounded-xl bg-sky-500/15 hover:bg-sky-500/25 border border-sky-500/40 text-sky-200 font-bold text-xs transition flex items-center justify-between active:scale-95"
                  >
                    <span className="flex items-center gap-2">
                      <Activity className="h-4 w-4 text-sky-400 shrink-0" />
                      <span>6. Cellular BP &amp; Scale</span>
                    </span>
                    <span className="text-[10px] font-mono text-sky-300">124/78 mmHg</span>
                  </button>
                </div>

                <div className="flex items-center justify-between pt-2 border-t border-slate-800/80">
                  <span className="text-[11px] text-slate-400">
                    Reset all room sensors and cloud webhook states back to normal baseline:
                  </span>
                  <button
                    onClick={() => {
                      setSensorTestStatus(null);
                      onResetHardwareAlert();
                    }}
                    className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold transition"
                  >
                    Reset All Room Sensors
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-800 bg-slate-950/90 flex items-center justify-between">
          <span className="text-[11px] font-mono text-slate-500">
            60GHz FMCW Spatial Doppler Telemetry + Multi-Sensor IoT Hub • {selectedRoomObj?.roomNumber || `Room ${selectedRoomId}`}
          </span>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold transition"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};
